import { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet.heat';
import { MapContainer, TileLayer, CircleMarker, Popup, GeoJSON, useMap } from 'react-leaflet';
import type { GeoJsonObject } from 'geojson';
import type { FireAlert, Hotspot, Incident, RegionSnapshot } from '@wildfire/core';

function sizeColor(acres: number): string {
  if (acres >= 10_000) return '#d11414';
  if (acres >= 1_000) return '#f25c1f';
  if (acres >= 100) return '#f6a15c';
  return '#f0d053';
}

function sizeRadius(acres: number): number {
  if (acres >= 10_000) return 12;
  if (acres >= 1_000) return 9;
  if (acres >= 100) return 6;
  return 4;
}

function HotspotHeat({ hotspots }: { hotspots: Hotspot[] }) {
  const map = useMap();
  const layerRef = useRef<L.Layer | null>(null);

  useEffect(() => {
    if (!hotspots.length) {
      if (layerRef.current) {
        map.removeLayer(layerRef.current);
        layerRef.current = null;
      }
      return;
    }

    const points = hotspots.map((h) => {
      // FRP (fire radiative power) is our intensity weight. Clamp so big
      // events don't blow out the gradient for everything else.
      const intensity = Math.min(1, (h.frp ?? 5) / 50);
      return [h.lat, h.lon, intensity] as [number, number, number];
    });

    const heat = (L as unknown as { heatLayer: typeof L.heatLayer }).heatLayer(points, {
      radius: 18,
      blur: 22,
      maxZoom: 10,
      minOpacity: 0.35,
      gradient: {
        0.2: '#2a6ed3',
        0.4: '#f6a15c',
        0.7: '#f25c1f',
        1.0: '#d11414',
      },
    }).addTo(map);

    layerRef.current = heat;
    return () => {
      map.removeLayer(heat);
      layerRef.current = null;
    };
  }, [map, hotspots]);

  return null;
}

function FitBoundsOnce({ bbox }: { bbox: [number, number, number, number] | null }) {
  const map = useMap();
  const appliedRef = useRef(false);
  useEffect(() => {
    if (!bbox || appliedRef.current) return;
    appliedRef.current = true;
    const [w, s, e, n] = bbox;
    map.fitBounds(
      [
        [s, w],
        [n, e],
      ],
      { padding: [24, 24] },
    );
  }, [bbox, map]);
  return null;
}

export interface MapViewProps {
  snapshot: RegionSnapshot | null;
  incidents: Incident[]; // filtered list
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  bbox: [number, number, number, number] | null;
}

export function MapView({ snapshot, incidents, selectedId, onSelect, bbox }: MapViewProps) {
  const alerts: FireAlert[] = snapshot?.alerts ?? [];
  const hotspots: Hotspot[] = snapshot?.hotspots ?? [];

  return (
    <MapContainer
      center={[37, -95]}
      zoom={4}
      scrollWheelZoom
      style={{ height: '100%', width: '100%' }}
    >
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />

      <FitBoundsOnce bbox={bbox} />

      {/* Alert polygons (Red Flag / Fire Weather Watch) */}
      {alerts.map((alert) =>
        alert.geometry ? (
          <GeoJSON
            key={alert.id}
            data={alert.geometry as GeoJsonObject}
            style={{
              color: '#f0d053',
              weight: 1,
              fillColor: '#f0d053',
              fillOpacity: 0.15,
            }}
          />
        ) : null,
      )}

      {/* Perimeters — semi-transparent red with thicker stroke for low containment */}
      {incidents
        .filter((i) => i.perimeter)
        .map((i) => (
          <GeoJSON
            key={`perim-${i.id}`}
            data={i.perimeter as unknown as GeoJsonObject}
            style={{
              color: '#d11414',
              weight: Math.max(1, 3 - (i.size.percentContained ?? 0) / 40),
              fillColor: '#d11414',
              fillOpacity: 0.2,
            }}
          />
        ))}

      <HotspotHeat hotspots={hotspots} />

      {/* Incident markers, sized/colored by acres */}
      {incidents.map((i) => (
        <CircleMarker
          key={i.id}
          center={[i.location.lat, i.location.lon]}
          radius={sizeRadius(i.size.acres)}
          pathOptions={{
            color: selectedId === i.id ? '#ffffff' : sizeColor(i.size.acres),
            weight: selectedId === i.id ? 2 : 1,
            fillColor: sizeColor(i.size.acres),
            fillOpacity: 0.9,
          }}
          eventHandlers={{ click: () => onSelect(i.id) }}
        >
          <Popup>
            <div className="popup">
              <h3>{i.name}</h3>
              <div className="meta">
                {i.location.county ? `${i.location.county}, ` : ''}
                {i.location.state}
              </div>
              <table>
                <tbody>
                  <tr>
                    <td>Acres</td>
                    <td>{i.size.acres.toLocaleString()}</td>
                  </tr>
                  <tr>
                    <td>Contained</td>
                    <td>{i.size.percentContained ?? '—'}%</td>
                  </tr>
                  <tr>
                    <td>Status</td>
                    <td>{i.status}</td>
                  </tr>
                  <tr>
                    <td>Cause</td>
                    <td>{i.cause}</td>
                  </tr>
                  {i.behavior ? (
                    <tr>
                      <td>Behavior</td>
                      <td>{i.behavior}</td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
              {i.sourceUrl ? (
                <div style={{ marginTop: 6 }}>
                  <a href={i.sourceUrl} target="_blank" rel="noreferrer">
                    InciWeb ↗
                  </a>
                </div>
              ) : null}
            </div>
          </Popup>
        </CircleMarker>
      ))}
    </MapContainer>
  );
}
